import {
  GridCellProps,
  GridColumn as Column,
} from '@progress/kendo-react-grid';
import {
  DateCell,
  FilterBarMainGrid,
  LinkCell,
  DateLinkCell,
  ReportsFilesDownloadIcon,
} from 'Components/Grid';
import {
  FormProvider,
  GridProvider,
  useEnums,
  useGlobalContext,
  useGridState,
} from 'Context';
import {
  ColumnSelectorProvider,
  useColumnSelectorState,
} from 'Context/ColumnSelectorProvider';
import {
  defaultGridProps,
  FilterBarFilterType,
  FilterConfig,
  IFilterConfig,
  StaticFilterConfig,
} from 'Models';
import { DefaultColumnsSelectionClosedReferrals } from 'Models/ColumnSelection';
import { ColumnMenu } from 'Components/Grid/TableHeader/ColumnMenu';

import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import {
  AddEditSubRoute,
  AppointmentSubRoute,
  ClaimantsSubRoute,
  ClaimsSubRoute,
  ROUTES,
} from 'Navigation';
import React, { Fragment, useEffect, useState } from 'react';
import {
  DEFAULT_DATE_FORMAT,
  buildEndpoint,
  useGridSearch,
  buildRoute,
  DEFAULT_DATETIME24H_FORMAT,
  useDataFetcher,
} from 'Utilities';
import NewReferralButton from '../../v3/Components/Shared/NewReferralButton';
import ClientSelector from 'v3/Components/Shared/ClientSelector';
import { useHistory } from 'react-router-dom';
import { DebouncedTextFilterCell } from 'Components/Grid/FilterCells/DebouncedTextFilterCell';
import { DatePickerFilterCell } from 'Components/Grid/FilterCells/DatePickerFilterCell';
import { DropdownFilterCell } from 'Components/Grid/FilterCells/DropdownFilterCell';
import { DropdownDbOption } from 'v3/Components/Form/Dropdown';
import DownloadIcon from '../../Assets/Icons/download.svg';
import { referralStatusCell } from 'v3/Components/Shared/StatusPill';
import { DateRangePickerFilterCell } from 'Components/Grid/FilterCells/DateRangePickerFilterCell';

class ClosedReferralsSearch {
  searchKey?: string;
  id?: string;
  client?: string;
  benefitState?: string;
  appointmentDate?: Date;
}

export enum ClosedReferralsGridIds {
  DetailedSearch = 'detailedSearch',
}

export const ClosedReferrals: React.FC = () => {
  const style = {
    marginTop: '0.5em',
    display: 'block',
  };
  return (
    <ColumnSelectorProvider
      columns={DefaultColumnsSelectionClosedReferrals}
      columnSelectorName="closedReferralsColumns"
    >
      <GridProvider entityName="Referral">
        <div className="main-grid-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h1>Closed Referrals</h1>
            <div className="grids-header-client-selector">
              <ClientSelector containerClassName="client-selector--large" />
            </div>
          </div>

          <NewReferralButton />
        </div>
        <br />
        <FilterBarDetailedSearchClosedReferralsGrid />
      </GridProvider>
    </ColumnSelectorProvider>
  );
};

export const ClosedTotalHeader: React.FC = () => {
  const gridState = useGridState();

  let closedTotals = `${gridState.total || 0}`;

  return (
    <Fragment>
      <div className="d-flex flex-row-reverse m-3">
        Grayed out Rows = Not Reviewed Closed Referrals.
      </div>
      <h6>{closedTotals} Appointment(s) Closed</h6>
    </Fragment>
  );
};

export const FilterBarDetailedSearchClosedReferralsGrid: React.FC = () => {
  const ClosedReferralSearch = new ClosedReferralsSearch();

  return (
    <FormProvider formModel={ClosedReferralSearch}>
      <ClosedReferralsGrid />
    </FormProvider>
  );
};

const ClosedReferralsGrid: React.FC = () => {
  const { state, lineOfBusiness } = useEnums();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isClient = userdata?.isClient ?? false;
  const isInternal = userdata?.isInternal ?? false;
  const isMedicalDirector = userdata?.isMedicalDirector ?? false;
  const isAccountsManager = userdata?.isAccountsManager ?? false;
  const history = useHistory();
  const columnSelectorState = useColumnSelectorState();
  const showDateFilters = userdata?.isInternal || userdata?.isAdmin;

  const [exportEndpoint, setExportEndpoint] = useState(
    buildEndpoint(
      ENDPOINTS.closedcases.endpointString('ExportToCsv') +
        `${getSelectedColumnsQuery(columnSelectorState)}`
    )
  );

  useEffect(() => {
    setExportEndpoint(
      buildEndpoint(
        ENDPOINTS.closedcases.endpointString('ExportToCsv') +
          `${getSelectedColumnsQuery(columnSelectorState)}`
      )
    );
  }, [columnSelectorState]);

  let searchBarHint: string =
    'Stradix Number, Claim Number, Claimant, Referral Number, Doctor';

  if (userdata != null && userdata.isAdmin) {
    searchBarHint = searchBarHint + ', External Adjuster';
  }

  const filters: IFilterConfig[] = [
    new FilterConfig(
      'searchKey',
      FilterBarFilterType.SearchText,
      searchBarHint
    ),
    new FilterConfig(
      'clients',
      FilterBarFilterType.AsyncDropdown,
      'Client',
      {
        readEndpoint: ENDPOINTS.clients.dropdownItems,
      },
      (value) => value.name,
      true
    ),
    new FilterConfig(
      'benefitStates',
      FilterBarFilterType.EnumDropdown,
      'State of Jurisdiction',
      { data: state },
      (value) => value.displayName,
      true
    ),
    new FilterConfig(
      'appointmentDate',
      FilterBarFilterType.DatePicker,
      'Appointment Date'
    ),
    new FilterConfig(
      'reviewers',
      FilterBarFilterType.AsyncDropdown,
      'Reviewer',
      {
        readEndpoint: `${ENDPOINTS.users.endpointString(
          'DropdownItems'
        )}?IsReviewer=true`,
        useQuery: true,
      },
      (value) => value.name,
      true
    ),
    new FilterConfig(
      'closedDateFrom',
      FilterBarFilterType.DatePicker,
      'Case Closed Date From'
    ),
    new FilterConfig(
      'closedDateTo',
      FilterBarFilterType.DatePicker,
      'Case Closed Date To'
    ),
    new StaticFilterConfig('clientId'),
  ];

  const [gridToggler] = useGridSearch();
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const dateColumnWidth = filtersEnabled ? '260px' : '200px';

  function navigateToReferral(id: string) {
    if (id != null) {
      history.push(buildRoute(AddEditSubRoute.Referral, id, ROUTES.VIEW));
    }
  }

  type FilterModel = {
    services?: DropdownDbOption[];
    submittedBy?: DropdownDbOption[];
    subServices?: DropdownDbOption[];
    status?: DropdownDbOption[];
  };
  const [filtersModel, filtersStatus] = useDataFetcher<FilterModel>(
    `${ENDPOINTS.closedcases.base}/GetFiltersModel`,
    {},
    (d) => d.data,
    undefined,
    true,
    '3.0'
  );

  const referralNumberCell: React.FC<GridCellProps> = (
    props: GridCellProps
  ) => {
    function referralRoute(id: string) {
      return buildRoute(AddEditSubRoute.Referral, id, ROUTES.VIEW);
    }
    const endpoint = `${ENDPOINTS.appointmentreports.base}/ClosedReferral`;
    return (
      <LinkCell
        to={referralRoute(props.dataItem.id)}
        style={{ borderRight: '1px solid #e0e0e0 !important' }}
        bold
        linkField="referralId"
        subRoute={ClaimsSubRoute.View}
        {...props}
      >
        {!(isInternal || isAccountsManager || isMedicalDirector) && (
          <ReportsFilesDownloadIcon
            fileControllerEndpoint={endpoint}
            display={<img src={DownloadIcon} alt="downloadIcon" />}
            {...props}
            id={props.dataItem.id}
            filename={props.dataItem.reportFileName}
          />
        )}
      </LinkCell>
    );
  };

  return (
    <React.Fragment>
      <FilterBarMainGrid
        gridId="ClosedReferrals"
        isMainGrid
        headerTitle="Referrals"
        style={{ cursor: 'pointer' }}
        onRowClick={(e) => {
          navigateToReferral(e.dataItem?.id);
        }}
        filterBarProps={{
          filterConfigurations: filters,
          filterValues: {},
          isColumnSelectorEnabled: false,
        }}
        apiVersion="3.0"
        refreshToggle={gridToggler}
        readEndpoint={ENDPOINTS.closedcases.read}
        exportEndpoint={`${ENDPOINTS.closedcases.base}/exportToCSV`}
        exportEndpointVersion="3.0"
        enableGridRefresh={true}
        defaultFiltersEnabled={true}
        onFiltersEnabledChange={setFiltersEnabled}
        {...defaultGridProps}
      >
        <Column
          field="stradixNumber"
          cell={referralNumberCell}
          title="Stradix Number"
          locked={true}
          filterable
          filterCell={DebouncedTextFilterCell}
          width="180px"
          columnMenu={ColumnMenu}
          className="column-stradix-number"
          reorderable={false}
        />
        <Column
          field="claimantName"
          title="Claimant Name"
          cell={claimantCell}
          filterable
          filterCell={DebouncedTextFilterCell}
          width="180px"
          columnMenu={ColumnMenu}
        />
        {(userdata?.isInternal ||
          userdata?.isDoctorAdmin ||
          userdata?.isDoctor ||
          userdata?.isReviewer) && (
          <Column
            field="client"
            title="Client Name"
            filterCell={DebouncedTextFilterCell}
            width="180px"
            columnMenu={ColumnMenu}
          />
        )}
        <Column
          field="closedDate"
          title="Case Closed Date"
          cell={dateAndTimeFormatCell}
          filterable
          filterCell={(e) => <DateRangePickerFilterCell {...e} />}
          width={dateColumnWidth}
          columnMenu={ColumnMenu}
        />
        <Column
          field="claimNumber"
          title="Claim/Client Number"
          cell={claimNumberCell}
          filterable
          filterCell={DebouncedTextFilterCell}
          width="188px"
          columnMenu={ColumnMenu}
        />
        <Column
          field="referralDate"
          title="Referral Date & Time"
          cell={dateAndTimeFormatCell}
          filterable
          filterCell={(e) => <DateRangePickerFilterCell {...e} />}
          width={dateColumnWidth}
          columnMenu={ColumnMenu}
        />
        <Column
          field="benefitState"
          title="State of Jurisdiction"
          width="140px"
          filterable
          filterCell={(x) => DropdownFilterCell(x, state)}
          columnMenu={ColumnMenu}
        />
        {!isClient && (
          <Column
            field="status"
            title="Status"
            width="180px"
            filterable
            filterCell={(x) =>
              DropdownFilterCell(
                x,
                undefined,
                filtersStatus === 'success' ? filtersModel.status : []
              )
            }
            cell={(e) =>
              referralStatusCell({
                ...e,
                onClick: () => navigateToReferral(e.dataItem?.id),
              })
            }
            columnMenu={ColumnMenu}
          />
        )}
        <Column
          field="service"
          title="Service"
          width="140px"
          filterable
          filterCell={(x) =>
            DropdownFilterCell(
              x,
              undefined,
              filtersStatus === 'success' ? filtersModel.services : []
            )
          }
          columnMenu={ColumnMenu}
        />
        <Column
          field="subService"
          title="Sub Service"
          width="140px"
          filterable
          filterCell={(x) =>
            DropdownFilterCell(
              x,
              undefined,
              filtersStatus === 'success' ? filtersModel.subServices : []
            )
          }
          columnMenu={ColumnMenu}
        />
        <Column
          field="lineOfBusinessName"
          title="Line Of Business"
          cell={lineOfBusinessCell}
          filterable
          filterCell={(x) => DropdownFilterCell(x, lineOfBusiness)}
          width="180px"
          columnMenu={ColumnMenu}
        />
        <Column
          field="referralSource"
          title="Submitted By"
          width="168px"
          filterable
          filterCell={(x) =>
            DropdownFilterCell(
              x,
              undefined,
              filtersStatus === 'success' ? filtersModel.submittedBy : []
            )
          }
          columnMenu={ColumnMenu}
        />
        {(isInternal || isAccountsManager) && (
          <Column
            field="ownerName"
            title="Owner"
            width="160px"
            columnMenu={ColumnMenu}
            filterCell={DebouncedTextFilterCell}
          />
        )}
        <Column width={0}></Column>
      </FilterBarMainGrid>
    </React.Fragment>
  );
};

const linkDateColumn: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <DateLinkCell
      endpoint={ROUTES.APPOINTMENT}
      linkField="id"
      subRoute={AppointmentSubRoute.View}
      dateFormat={DEFAULT_DATE_FORMAT}
      {...props}
    />
  );
};

const claimNumberCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  function claimRoute(id: string) {
    return buildRoute(ROUTES.CLAIM, id, ROUTES.VIEW);
  }
  return (
    <LinkCell
      {...props}
      to={claimRoute(props.dataItem.claimId)}
      linkField="claimId"
      subRoute={ClaimsSubRoute.View}
      className="claimNumber"
    ></LinkCell>
  );
};

const lineOfBusinessCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <td>{props.dataItem.lineOfBusiness?.displayName}</td>;
};

const claimantCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <LinkCell
      {...props}
      endpoint={ROUTES.CLAIMANT}
      linkField="claimantId"
      subRoute={ClaimantsSubRoute.View}
      className="claimantName"
    ></LinkCell>
  );
};

const dateAndTimeFormatCell: React.FC<GridCellProps> = (
  props: GridCellProps
) => {
  return <DateCell {...props} dateFormat={DEFAULT_DATETIME24H_FORMAT} />;
};

const getSelectedColumnsQuery = (columnSelectorState: any) => {
  const selectedColumns = columnSelectorState.columns
    .filter((x: any) => x.checked)
    .map((x: any) => x.field);
  return '?' + selectedColumns.map((x: any) => `columns=${x}`).join('&');
};
